// Pure helpers for inspecting captured camera frames before they're shipped
// to the backend / GPT vision. The camera occasionally returns visually-black
// frames (lens covered, stalled stream after rapid restart). Sending those to
// the scene classifier produces a string of "None" classifications and trips
// the consecutive-None threshold, surfacing the "keep acting normal" message.

const DEFAULT_SAMPLE_STRIDE = 64;
const DEFAULT_LUMINANCE_THRESHOLD = 8;

// Rec. 709 luminance approximation. Operates on a sampled subset of pixels
// (one in every `sampleStride`) for speed — at stride 64 over 640x480 we
// inspect ~4,800 samples, far more than enough to catch a uniformly-dark
// frame while staying under a millisecond.
//
// Returns true if the mean luminance across samples is at or below the
// threshold. A blocked lens / stalled stream produces values ~0–3; even a
// dimly-lit indoor scene at night clears 25+. Threshold of 8 keeps real
// content in and only rejects near-black noise.
export function isFrameTooDark(imageData, options = {}) {
  if (!imageData || !imageData.data || imageData.data.length === 0) {
    return true;
  }
  const sampleStride = options.sampleStride || DEFAULT_SAMPLE_STRIDE;
  const threshold = options.threshold == null ? DEFAULT_LUMINANCE_THRESHOLD : options.threshold;
  const pixels = imageData.data;
  const stepBytes = Math.max(4, sampleStride * 4);

  let total = 0;
  let count = 0;
  for (let i = 0; i < pixels.length; i += stepBytes) {
    // Rec. 709 weights: 0.2126 R + 0.7152 G + 0.0722 B
    total += pixels[i] * 0.2126 + pixels[i + 1] * 0.7152 + pixels[i + 2] * 0.0722;
    count += 1;
  }
  if (count === 0) return true;
  return total / count <= threshold;
}
